/**
 * @file RecorderDock.tsx
 * @brief The Boardroom recorder's controls as one dock along the bottom of a
 *        large screen, its border the minute timer.
 *
 * @details
 * DES 11-10-2026: "agar is screen per hum dock bana dain", then "haan
 * preview main banao" on the mockup. The standard since W10 (22-10-2026):
 * every window size, web and Mac (recorderDockOn). Under lg it is `compact`:
 * the same dock across the window's width, Key decision / To-do as icons,
 * a shorter slide, and under sm no title (the page heading shows it).
 *   - Above the dock, one quiet line the old sheet showed under the dial:
 *     the recorder's error, else the minutes line (Free minutes, included
 *     minutes used, fair use; `note`). Wraps, never cut: the used-up
 *     sentence says the recording keeps going.
 *   - Left to right: the hold button (dot / pause), "Recording" over the
 *     title (the time is only on the border's pill), the avatars, Key decision and To-do, the compact slide, ⋯ with
 *     Discard (harder to hit by mistake than a link under the slide).
 *   - The border IS the timer: it fills clockwise from the top centre once a
 *     minute (`elapsedMs % 60s`), the same rule as ListeningMicButton's ring,
 *     and the time badge rides its tip. Hold freezes both and greys them.
 *   - Meeting Notes: no marks and no avatars (nothing to press; shared with no
 *     one), as the sheet.
 *
 * Fixed at the bottom of the page, centred, as wide as the content column
 * (`anchor`), just above the shell footer.
 *
 * @references
 * - ListeningMicButton.tsx (ring rule), BoardroomRecorderPage.tsx (host)
 *
 * @author DES
 * @date 11-10-2026
 */

import { useEffect, useState } from "react";
import { Bookmark, CircleCheckBig, Ellipsis, Trash2 } from "lucide-react";
import { Avatar } from "@/components/ui/Avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import { SwipeButton } from "@/components/ui/SwipeButton";
import { DockBorderTimer } from "./DockBorderTimer";
import { RecorderAgendaPill } from "./RecorderAgendaPill";
import type { AgendaCheck } from "./agendaChecks";
import { useMicGlow } from "./useMicGlow";
import { cn } from "@/shared/theme/cn";

const DOCK_H = 60;
/** The dock keeps its own width when the page under it runs wider (DES 11-10-2026; 1024, then "reduce the width a bit"). */
const DOCK_MAX_W = 880;

type Person = { key: string; name: string; photo?: string | null; initials: string };

export type RecorderDockProps = {
  anchor: HTMLElement | null;
  title: string;
  status: "idle" | "recording" | "held" | "stopping" | "error";
  elapsedMs: number;
  elapsedLabel: string;
  people: Person[];
  isNotes: boolean;
  canBookmark: boolean;
  uploading: boolean;
  onMicTap: () => void;
  onAddBookmark: (type: "decision" | "todo") => void;
  onEnd: () => void;
  onDiscard: () => void;
  /** Live mic level 0..100 (the page's meter), for the glow. */
  audioLevelRef: { current: number };
  /** Under lg: the small-window dock. */
  compact?: boolean;
  /** Free / included minutes or fair-use line (W7, B5, W9). */
  note?: string | null;
  /** The recorder's error, shown in place of the note. */
  error?: string | null;
  /** B49: the agenda check boxes; null / absent = no pill. */
  agendaChecks?: AgendaCheck[] | null;
  /** Ticks are frozen once the recording stops. */
  agendaLocked?: boolean;
  onAgendaToggle?: (key: string) => void;
};

export function RecorderDock(props: RecorderDockProps) {
  const {
    anchor,
    title,
    status,
    elapsedMs,
    elapsedLabel,
    people,
    isNotes,
    canBookmark,
    uploading,
    onMicTap,
    onAddBookmark,
    onEnd,
    onDiscard,
    audioLevelRef,
    compact = false,
    note,
    error,
    agendaChecks,
    agendaLocked = false,
    onAgendaToggle,
  } = props;
  const held = status === "held";

  // Sit over the content column, just above the shell footer.
  const [box, setBox] = useState<{ left: number; width: number; bottom: number } | null>(null);
  useEffect(() => {
    if (!anchor) return;
    const footerH = document.querySelector("footer")?.offsetHeight ?? 0;
    const measure = () => {
      // Centred on the page, at most DOCK_MAX_W (DES 11-10-2026: "dock ko
      // center of the page karo"; kept at its first size, "dock ka size
      // purana karo").
      const r = anchor.getBoundingClientRect();
      const page = document.documentElement.clientWidth;
      // Small windows: the window's width less a 16px gutter each side.
      const width = compact
        ? Math.min(DOCK_MAX_W, page - 32)
        : Math.min(r.width, DOCK_MAX_W, page - 48);
      setBox({ left: (page - width) / 2, width, bottom: footerH + 14 });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(anchor);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [anchor, compact]);


  // Mic glow behind the hold button, recording only (useMicGlow, shared
  // with the desktop widget).
  const glowRef = useMicGlow(audioLevelRef, status === "recording" && !uploading);

  // B49: the agenda pill sits on the dock's top edge, at its left; the row
  // itself is unchanged (in the row it squeezed the title to nothing).
  const agendaPill = !isNotes && agendaChecks && onAgendaToggle;

  const shown = people.slice(0, 2);
  const more = people.length - shown.length;

  return (
    <div
      className="fixed z-30 animate-in fade-in-0 slide-in-from-bottom-4"
      style={box ? { left: box.left, width: box.width, bottom: box.bottom } : { visibility: "hidden" }}
    >
      {agendaPill ? (
        <div className="absolute bottom-full left-2 mb-2.5">
          <RecorderAgendaPill
            checks={agendaChecks}
            locked={agendaLocked || uploading}
            onToggle={onAgendaToggle}
          />
        </div>
      ) : null}
      {error || note ? (
        <p
          role={error ? "alert" : "status"}
          className={cn(
            "dock-glass absolute bottom-full left-1/2 mb-2.5 w-max -translate-x-1/2",
            // Clear of the agenda pill at the left (B49).
            agendaPill ? "max-w-[calc(100%-16rem)]" : "max-w-full",
            "text-balance rounded-2xl px-3 py-1 text-center text-[12px] font-medium leading-snug",
            error ? "text-destructive" : "text-muted-foreground",
          )}
        >
          {error || note}
        </p>
      ) : null}
      <div
        // THE dock glass, as every other dock (dockGlass.ts, DES 11-10-2026:
        // "kia yeh dock bhi glassi hai"). Its CSS border is left off: the
        // drawn track below is the edge, and the timer runs on it.
        className="dock-glass relative rounded-full shadow-[0_8px_30px_rgba(0,0,0,0.12)] dark:shadow-none"
        style={{ height: DOCK_H }}
      >
        <DockBorderTimer
          elapsedMs={elapsedMs}
          label={elapsedLabel}
          held={held}
          showBadge={status === "recording" || held}
          radius="pill"
        />

        <div className={cn("relative flex h-full items-center pl-3 pr-3", compact ? "gap-2 sm:gap-3" : "gap-3")}>
          <span className="relative flex shrink-0 items-center justify-center">
          <span
            ref={glowRef}
            aria-hidden
            className="pointer-events-none absolute inset-0 rounded-full bg-brand blur-md"
            style={{ opacity: 0, transform: "scale(1)", willChange: "transform, opacity" }}
          />
          <button
            type="button"
            onClick={onMicTap}
            disabled={uploading}
            aria-label={held ? "Resume recording" : "Hold recording"}
            title={held ? "Tap to resume" : "Tap to hold"}
            className={cn(
              "relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 bg-secondary transition-colors disabled:opacity-50 dark:bg-card",
              held ? "border-muted-foreground/60" : "border-brand",
            )}
          >
            {held ? (
              <span className="flex gap-1" aria-hidden>
                <span className="h-3.5 w-1 rounded-full bg-muted-foreground" />
                <span className="h-3.5 w-1 rounded-full bg-muted-foreground" />
              </span>
            ) : (
              <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-brand" aria-hidden />
            )}
          </button>
          </span>

          <div className={cn("min-w-0 max-w-[18rem]", compact && "hidden flex-1 sm:block")}>
            <p
              className={cn(
                "flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.12em]",
                held ? "text-muted-foreground" : "text-brand",
              )}
            >
              <span
                aria-hidden
                className={cn("h-1.5 w-1.5 rounded-full", held ? "bg-muted-foreground" : "bg-brand")}
              />
              {/* No time here: the pill on the border shows it (DES 11-10-2026). */}
              {held ? "On hold" : "Recording"}
            </p>
            {/* Two lines, then "…" (DES 11-10-2026: "lamba title two line main karo"). */}
            <p className="line-clamp-2 break-words text-sm font-semibold leading-[1.15] text-foreground/70" title={title}>
              {title}
            </p>
          </div>

          {!isNotes && people.length > 0 ? (
            <div className={cn("flex shrink-0 items-center -space-x-3", compact && "hidden sm:flex")}>
              {shown.map((p) => (
                <Avatar
                  key={p.key}
                  src={p.photo}
                  alt={p.name}
                  initials={p.initials}
                  // The row's height (DES 11-10-2026: "why are these looking
                  // smaller"); were "sm" (28px).
                  size="lg"
                  className="h-10 w-10 text-xs ring-2 ring-background/80"
                />
              ))}
              {more > 0 ? (
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-xs font-semibold ring-2 ring-background/80">
                  +{more}
                </span>
              ) : null}
            </div>
          ) : null}

          {/* One height for every control in the row: 40px in a 60px dock,
              the slide's and avatars' too (DES 11-10-2026: one height, then
              "reduce the overall size a bit"; the widget's row is 36px). */}
          {!isNotes ? (
            <>
              <span aria-hidden className={cn("mx-1 h-8 w-px shrink-0 bg-border", compact && "hidden sm:block")} />
              {/* Compact: icons only, the label as the name and tooltip. */}
              <button
                type="button"
                disabled={!canBookmark}
                onClick={() => onAddBookmark("decision")}
                aria-label="Key Decision"
                title={compact ? "Key Decision" : undefined}
                className={cn(
                  "inline-flex h-10 shrink-0 items-center justify-center gap-1.5 rounded-full bg-brand/[0.05] text-[13px] font-bold text-brand transition-colors hover:bg-brand/15 disabled:pointer-events-none disabled:opacity-50",
                  compact ? "w-10" : "px-3.5",
                )}
              >
                <Bookmark className="h-4 w-4 fill-brand text-brand" />
                {compact ? null : "Key Decision"}
              </button>
              <button
                type="button"
                disabled={!canBookmark}
                onClick={() => onAddBookmark("todo")}
                aria-label="To-do"
                title={compact ? "To-do" : undefined}
                className={cn(
                  "inline-flex h-10 shrink-0 items-center justify-center gap-1.5 rounded-full bg-background text-[13px] font-bold text-foreground transition-colors hover:bg-muted disabled:pointer-events-none disabled:opacity-50",
                  compact ? "w-10" : "px-3.5",
                )}
              >
                <CircleCheckBig className="h-4 w-4 text-foreground" />
                {compact ? null : "To-do"}
              </button>
            </>
          ) : null}

          <div className={cn("ml-auto shrink-0", compact ? "w-36 sm:w-48" : "w-52")}>
            <SwipeButton
              size="compact"
              text="Slide to end"
              loading={uploading}
              loadingText="Processing…"
              disabled={uploading || status === "idle"}
              onSwipeComplete={onEnd}
              accessibilityLabel={
                isNotes ? "End recording and write notes" : "End recording and process AI"
              }
              accessibilityHint="Slide or press Enter to end the meeting"
            />
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="More"
                disabled={uploading}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50"
              >
                <Ellipsis className="h-5 w-5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" side="top" className="w-48 p-1.5">
              <DropdownMenuItem destructive onSelect={onDiscard}>
                <Trash2 className="h-4 w-4" />
                Discard recording
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
}

export default RecorderDock;
